import React from 'react';
import Select from 'react-select';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
import Modal from '../../../design/shared/modal';
import { FieldInput, FieldSelect } from '../../fields';
import { Button } from '../../fields/css';
import {
  orderTypeOptions,
  customerTypeOptions,
  lineDesignationOptions,
  lineTypeOptions,
  callingFeatureOptions,
  tollFreeOptions,
  addOnsOptions,
  initialFormValues,
  sectionStyles
} from './config';
import {
  getAvailableTerms,
  getAvailablePlans,
  shouldShowCustomPricing,
  customPricingOptions,
  handleFieldChange,
  getAvailableLineTypes,
  shouldShowNumberOfLines,
  shouldShowPhoneNumber,
  shouldShowOneFeature,
  shouldShowUnlimited,
  getAvailableAddOns,
  getIncludedAddOns,
  validateForm
} from './formLogics';

const OfficePhoneLinePopup = ({ 
  isOpen, 
  onClose, 
  onAddLine,
  editingLine = null  // Add this prop
}) => {
  // Use editingLine data if available, otherwise use initial values
  const initialValues = editingLine ? {
    orderType: editingLine.orderType || '',
    term: editingLine.term || '',
    plan: editingLine.plan || '',
    customPrice: editingLine.customPrice || '',
    customerType: editingLine.customerType || '',
    lineDesignation: editingLine.lineDesignation || '',
    lineType: editingLine.lineType || '',
    numberOfLines: editingLine.numberOfLines || '',
    phoneNumbers: editingLine.phoneNumbers || '',
    callingFeature1: editingLine.callingFeature1 || '',
    callingFeatureUnlimited: editingLine.callingFeatureUnlimited || [],
    tollFreeNumber: editingLine.tollFreeNumber || '',
    addOns: editingLine.addOns || []
  } : initialFormValues;

  const handleSubmit = (values, { setSubmitting }) => {
    // Preserve the ID if editing
    const lineData = {
      ...values,
      id: editingLine ? editingLine.id : Date.now().toString()
    };
    
    onAddLine(lineData);
    setSubmitting(false);
    onClose();
  };

  return (
    <Modal
      open={isOpen}
      setOpen={onClose}
      header={editingLine ? "Edit Line Details" : "Line Details"}
    >
      <Formik
        initialValues={initialValues}
        enableReinitialize={true}
        validate={validateForm}
        validateOnBlur
        validateOnChange
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue, isSubmitting, submitForm, errors, touched, isValid }) => (
          <Form>
            <div style={{ padding: '20px' }}>
              <FieldSelect
                data={{
                  name: 'orderType',
                  label: 'Order Type',
                  value: values.orderType,
                  required: true,
                  onChange: (e) => {
                    handleFieldChange('orderType', e.target.value, values, setFieldValue);
                    setFieldValue('orderType', e.target.value);
                  },
                  options: orderTypeOptions.map(option => ({
                    fields: {
                      label: option.label,
                      value: option.value
                    }
                  })),
                }}
              />
              {errors.orderType && touched.orderType && (
                <div style={{ color: 'red', fontSize: '15px', marginTop: '5px' }}>
                  {errors.orderType}
                </div>
              )}
              
              <FieldSelect
                key={`term-${values.orderType}`}
                data={{
                  name: 'term',
                  label: 'Term',
                  value: values.term,
                  required: true,
                  onChange: (e) => {
                    handleFieldChange('term', e.target.value, values, setFieldValue);
                    setFieldValue('term', e.target.value);
                  },
                  options: getAvailableTerms(values.orderType).map(option => ({
                    fields: {
                      label: option.label,
                      value: option.value
                    }
                  })),
                }}
              />
              {errors.term && touched.term && (
                <div style={{ color: 'red', fontSize: '15px', marginTop: '5px' }}>
                  {errors.term}
                </div>
              )}
              
              <FieldSelect
                key={`plan-${values.orderType}-${values.term}`}
                data={{
                  name: 'plan',
                  label: 'Plan',
                  value: values.plan,
                  required: true,
                  onChange: (e) => {
                    handleFieldChange('plan', e.target.value, values, setFieldValue);
                    setFieldValue('plan', e.target.value);
                  },
                  options: getAvailablePlans(values.orderType, values.term).map(option => ({
                    fields: {
                      label: option.label,
                      value: option.value
                    }
                  })),
                }}
              />

              {errors.plan && touched.plan && (
                <div style={{ color: 'red', fontSize: '15px', marginTop: '5px' }}>
                  {errors.plan}
                </div>
              )}

              {/* Custom Price Field - Only shown for Office Phone Plus 36 months */}
              {shouldShowCustomPricing(values.orderType, values.term, values.plan) && (
                <>
                  <FieldSelect
                    key={`customPrice-${values.orderType}-${values.term}-${values.plan}`}
                    data={{
                      name: 'customPrice',
                      label: 'Price',
                      value: values.customPrice,
                      required: true,
                      onChange: (e) => setFieldValue('customPrice', e.target.value),
                      options: customPricingOptions.map(option => ({
                        fields: {
                          label: option.label,
                          value: option.value
                        }
                      })),
                    }}
                  />
                  {errors.customPrice && touched.customPrice && (
                    <div style={{ color: 'red', fontSize: '15px', marginTop: '5px' }}>
                      {errors.customPrice}
                    </div>
                  )}
                </>
              )}

              {/* Set Up Info Section */}
              <div style={sectionStyles.container}>
                <div style={sectionStyles.header}>
                  Set Up Info
                </div>
                
                <FieldSelect
                  data={{
                    name: 'customerType',
                    label: 'Customer Type',
                    value: values.customerType,
                    required: true,
                    onChange: (e) => {
                      handleFieldChange('customerType', e.target.value, values, setFieldValue);
                      setFieldValue('customerType', e.target.value);
                    },
                    options: customerTypeOptions.map(option => ({
                      fields: {
                        label: option.label,
                        value: option.value
                      }
                    })),
                  }}
                />
                {errors.customerType && touched.customerType && (
                  <div style={{ color: 'red', fontSize: '15px', marginTop: '5px' }}>
                    {errors.customerType}
                  </div>
                )}
                
                <FieldSelect
                  data={{
                    name: 'lineDesignation',
                    label: 'Line Designation',
                    value: values.lineDesignation,
                    required: true,
                    onChange: (e) => {
                      handleFieldChange('lineDesignation', e.target.value, values, setFieldValue);
                      setFieldValue('lineDesignation', e.target.value);
                    },
                    options: lineDesignationOptions.map(option => ({
                      fields: {
                        label: option.label,
                        value: option.value
                      }
                    })),
                  }}
                />
                {errors.lineDesignation && touched.lineDesignation && (
                  <div style={{ color: 'red', fontSize: '15px', marginTop: '5px' }}>
                    {errors.lineDesignation}
                  </div>
                )}
                
                <FieldSelect
                  key={`lineType-${values.lineDesignation}`}
                  data={{
                    name: 'lineType',
                    label: 'Line Type',
                    value: values.lineType,
                    required: true,
                    onChange: (e) => {
                      handleFieldChange('lineType', e.target.value, values, setFieldValue);
                      setFieldValue('lineType', e.target.value);
                    },
                    options: getAvailableLineTypes(values.lineDesignation).map(option => ({
                      fields: {
                        label: option.label,
                        value: option.value
                      }
                    })),
                  }}
                />
                {errors.lineType && touched.lineType && (
                  <div style={{ color: 'red', fontSize: '15px', marginTop: '5px' }}>
                    {errors.lineType}
                  </div>
                )}
                
                {/* Number of Lines - Only show if Line Type = Multi Line */}
                {shouldShowNumberOfLines(values.lineType) && (
                  <>
                    <FieldInput
                      data={{
                        name: 'numberOfLines',
                        type: 'Number',
                        label: 'Number of Lines',
                        value: values.numberOfLines,
                        required: true,
                        min: 1,
                        max: 10,
                        onChange: (e) => setFieldValue('numberOfLines', e.target.value),
                        note: '(1-10)'
                      }}
                    />
                    {errors.numberOfLines && touched.numberOfLines && (
                      <div style={{ color: 'red', fontSize: '15px', marginTop: '5px' }}>
                        {errors.numberOfLines}
                      </div>
                    )}
                  </>
                )}
                
                {/* Phone Number - Only show for winback or existing customers */}
                {shouldShowPhoneNumber(values.customerType) && (
                  <FieldInput
                    data={{
                      name: 'phoneNumbers',
                      type: 'Text',
                      label: 'Phone Number(s)',
                      value: values.phoneNumbers,
                      required: false,
                      onChange: (e) => setFieldValue('phoneNumbers', e.target.value),
                      note: '*For multi line add all phone numbers separated by commas'
                    }}
                  />
                )}
              </div>

              {/* Calling Features Section */}
              <div style={sectionStyles.container}>
                <div style={sectionStyles.header}>
                  Calling Features
                </div>
                
                {/* 1 Feature - Only show for Office Phone Basic */}
                {shouldShowOneFeature(values.plan) && (
                  <FieldSelect
                    data={{
                      name: 'callingFeature1',
                      label: '1  Feature',
                      value: values.callingFeature1,
                      required: false,
                      onChange: (e) => setFieldValue('callingFeature1', e.target.value),
                      options: callingFeatureOptions.map(option => ({
                        fields: {
                          label: option.label,
                          value: option.value
                        }
                      })),
                    }}
                  />
                )}
                
                {/* Unlimited - Only show for Office Phone and Office Phone Plus */}
                {shouldShowUnlimited(values.plan) && (
                  <div style={{ margin: '1em 0' }}>
                    <span className="frg-body-font-14px">
                      Unlimited
                    </span>
                    <div style={{ marginTop: '5px' }}>
                      <Select
                        isMulti
                        name="callingFeatureUnlimited"
                        placeholder="Select features..."
                        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                        value={callingFeatureOptions
                          .filter(option => values.callingFeatureUnlimited.includes(option.value))
                          .map(option => ({
                            label: option.label,
                            value: option.value
                          }))
                        }
                        onChange={(selectedOptions) => {
                          const selectedValues = selectedOptions ? selectedOptions.map(option => option.value) : [];
                          setFieldValue('callingFeatureUnlimited', selectedValues);
                        }}
                        options={callingFeatureOptions.map(option => ({
                          label: option.label,
                          value: option.value
                        }))}
                        styles={{
                          control: (baseStyles) => ({
                            ...baseStyles,
                            borderColor: '#54595f',
                            fontSize: '13px',
                            fontFamily: 'inherit',
                            minHeight: '2.3rem'
                          }),
                          input: (provided) => ({
                            ...provided,
                            height: '2.3rem',
                            fontSize: '13px',
                            fontFamily: 'inherit',
                          }),
                          option: (provided, state) => ({
                            ...provided,
                            fontSize: '13px',
                            fontFamily: 'inherit',
                            backgroundColor: (() => {
                              if (state.isSelected) return '#2B8000';
                              if (state.isFocused) return '#b2d47e';
                              return 'white';
                            })(),
                            color: state.isSelected ? 'white' : 'black',
                            cursor: 'pointer'
                          }),
                          menu: (provided) => ({
                            ...provided,
                            zIndex: 9999,
                            fontFamily: 'inherit',
                            fontSize: '13px'
                          }),
                          menuList: (provided) => ({
                            ...provided,
                            maxHeight: '200px',
                            overflowY: 'auto',
                          }),
                          menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
                        }}
                        theme={(theme) => ({
                          ...theme,
                          colors: {
                            ...theme.colors,
                            primary25: '#b2d47e',
                            primary: '#2B8000',
                            primary50: '#b2d47e'
                          }
                        })}
                      />
                    </div>
                  </div>
                )}
                
              </div>

              {/* Toll Free Number and Add Ons - Outside of sections */}
              <div style={{ marginTop: '20px' }}>
                <FieldSelect
                  data={{
                    name: 'tollFreeNumber',
                    label: 'Toll Free Number',
                    value: values.tollFreeNumber,
                    required: true,
                    onChange: (e) => setFieldValue('tollFreeNumber', e.target.value),
                    options: tollFreeOptions.map(option => ({
                      fields: {
                        label: option.label,
                        value: option.value
                      }
                    })),
                  }}
                />
                {errors.tollFreeNumber && touched.tollFreeNumber && (
                  <div style={{ color: 'red', fontSize: '15px', marginTop: '5px' }}>
                    {errors.tollFreeNumber}
                  </div>
                )}
                
                {/* Add Ons - Multi-select with plan-specific options and included items */}
                <div style={{ margin: '1em 0' }}>
                  <span className="frg-body-font-14px">
                    Add Ons
                  </span>
                  <div style={{ marginTop: '5px' }}>
                    <Select
                      isMulti
                      name="addOns"
                      placeholder="Select add-ons..."
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                      value={getAvailableAddOns(values.plan)
                        .filter(option => values.addOns.includes(option.value))
                        .map(option => ({
                          label: option.label,
                          value: option.value,
                          isIncluded: option.included
                        }))
                      }
                      onChange={(selectedOptions) => {
                        const selectedValues = selectedOptions ? selectedOptions.map(option => option.value) : [];
                        // Ensure included items are always present
                        const includedAddOns = getIncludedAddOns(values.plan);
                        const finalValues = [...new Set([...includedAddOns, ...selectedValues])];
                        setFieldValue('addOns', finalValues);
                      }}
                      options={getAvailableAddOns(values.plan).map(option => ({
                        label: option.label,
                        value: option.value,
                        isIncluded: option.included
                      }))}
                      isOptionDisabled={(option) => option.isIncluded}
                      onKeyDown={(event) => {
                        // Prevent backspace from removing included items
                        if (event.key === 'Backspace') {
                          const includedAddOns = getIncludedAddOns(values.plan);
                          const currentValues = values.addOns || [];
                          if (currentValues.length <= includedAddOns.length) {
                            event.preventDefault();
                          }
                        }
                      }}
                      styles={{
                        control: (baseStyles) => ({
                          ...baseStyles,
                          borderColor: '#54595f',
                          fontSize: '13px',
                          fontFamily: 'inherit',
                          minHeight: '2.3rem'
                        }),
                        input: (provided) => ({
                          ...provided,
                          height: '2.3rem',
                          fontSize: '13px',
                          fontFamily: 'inherit',
                        }),
                        option: (provided, state) => ({
                          ...provided,
                          fontSize: '13px',
                          fontFamily: 'inherit',
                          backgroundColor: (() => {
                            if (state.isSelected) return '#2B8000';
                            if (state.isFocused) return '#b2d47e';
                            return 'white';
                          })(),
                          color: state.isSelected ? 'white' : 'black',
                          cursor: state.isDisabled ? 'not-allowed' : 'pointer',
                          opacity: state.isDisabled ? 0.6 : 1
                        }),
                        menu: (provided) => ({
                          ...provided,
                          zIndex: 9999,
                          fontFamily: 'inherit',
                          fontSize: '13px'
                        }),
                        menuList: (provided) => ({
                          ...provided,
                          maxHeight: '200px',
                          overflowY: 'auto',
                        }),
                        multiValue: (provided, state) => ({
                          ...provided,
                          backgroundColor: state.data.isIncluded ? '#2B8000' : '#e3f2fd',
                          color: state.data.isIncluded ? 'white' : 'black'
                        }),
                        multiValueLabel: (provided, state) => ({
                          ...provided,
                          color: state.data.isIncluded ? 'white' : 'black'
                        }),
                        multiValueRemove: (provided, state) => ({
                          ...provided,
                          display: state.data.isIncluded ? 'none' : 'block',
                          color: state.data.isIncluded ? 'transparent' : '#666',
                          cursor: state.data.isIncluded ? 'default' : 'pointer'
                        }),
                        menuPortal: (provided) => ({ ...provided, zIndex: 9999 })
                      }}
                      theme={(theme) => ({
                        ...theme,
                        colors: {
                          ...theme.colors,
                          primary25: '#b2d47e',
                          primary: '#2B8000',
                          primary50: '#b2d47e'
                        }
                      })}
                    />
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <Button
                  type="button"
                  onClick={onClose}
                  className="frg-button color-purple"
                  id="cancel-button"
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={submitForm}
                  disabled={isSubmitting}
                  className="frg-button color-green"
                  id="add-line-button"
                >
                  {editingLine ? 'Update' : 'Add'}
                </Button>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

OfficePhoneLinePopup.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onAddLine: PropTypes.func.isRequired,
  editingLine: PropTypes.shape({})
};

export default OfficePhoneLinePopup;
